import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function FeministTermsGlossary() {
  const { t } = useTranslation();

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent("Feminist Terms & Signs You Should Know - Essential Glossary");

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c448148ad3038dec5ef3e0e7caadf546.jpg"
            alt="Feminist Education"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <div className="inline-block px-4 py-2 bg-purple-600 rounded-full text-sm font-medium mb-6">
            Essential Knowledge
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Feminist Terms & Signs You Should Know
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            Your Essential Glossary to Understanding Feminism, Patriarchy, and Women's Rights
          </p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <span className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              December 3, 2025
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              12 min read
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-book-line"></i>
              Education
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        
        {/* Personal Note */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-purple-600 rounded-lg p-8 mb-12">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-heart-3-fill text-3xl text-purple-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Why I'm Writing This</h3>
              <div className="text-gray-700 space-y-3 leading-relaxed">
                <p>
                  For years, I heard these words—patriarchy, intersectionality, glass ceiling—and nodded along, 
                  pretending I understood. But I didn't. Not really.
                </p>
                <p>
                  I didn't know that what I was experiencing had a name. That the voice in my head telling me 
                  I wasn't good enough? That's internalized misogyny. That feeling of being stuck no matter 
                  how hard I worked? That's the glass ceiling. That exhaustion from doing all the housework 
                  while also working full-time? That's the second shift.
                </p>
                <p>
                  <strong>Once I learned these terms, everything clicked.</strong> I could finally name what 
                  I was experiencing. I could finally understand that it wasn't just me—it was the system.
                </p>
                <p>
                  So I'm writing this glossary for every woman who's ever felt confused, who's ever nodded 
                  along without understanding, who's ever wanted to join the conversation but didn't know 
                  the language.
                </p>
                <p className="font-semibold text-purple-700">
                  This is your feminist dictionary. Learn these terms. Use them. Share them. Because once 
                  you can name it, you can fight it. 💜✊
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-700 leading-relaxed">
            Feminism has its own language—and understanding that language is the first step to understanding 
            the movement. This glossary explains the essential terms, concepts, and signs you need to know 
            to navigate feminist spaces, recognize oppression, and fight for equality.
          </p>
        </div>

        {/* Core Feminist Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-book-open-line text-3xl text-purple-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Core Feminist Terms</h2>
          </div>

          <div className="space-y-8">
            {/* Feminism */}
            <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                <i className="ri-women-line text-purple-600 mr-2"></i>
                Feminism
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>What it means:</strong> The belief that all genders deserve equal rights, opportunities, 
                and treatment. Feminism fights against systems that oppress women and advocates for gender equality.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>What it's NOT:</strong> Hating men, wanting women to be "better" than men, or rejecting 
                femininity. Feminism is about equality, not superiority.
              </p>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600 italic">
                  "Feminism is the radical notion that women are people." — Marie Shear
                </p>
              </div>
            </div>

            {/* Patriarchy */}
            <div className="bg-red-50 rounded-xl p-6 border-l-4 border-red-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                <i className="ri-government-line text-red-600 mr-2"></i>
                Patriarchy
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>What it means:</strong> A social system where men hold primary power and dominate in roles 
                of political leadership, moral authority, social privilege, and control of property.
              </p>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>How it shows up:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Men making decisions about women's bodies (abortion laws)</li>
                <li>Women doing most unpaid domestic labor</li>
                <li>Leadership positions dominated by men</li>
                <li>Women's work being valued less than men's</li>
                <li>Boys being taught not to cry, girls being taught to be "nice"</li>
              </ul>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Important:</strong> Patriarchy hurts everyone—including men. It forces men into rigid 
                  roles and punishes them for showing emotion or vulnerability.
                </p>
              </div>
            </div>

            {/* Internalized Misogyny */}
            <div className="bg-pink-50 rounded-xl p-6 border-l-4 border-pink-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                <i className="ri-mind-map text-pink-600 mr-2"></i>
                Internalized Misogyny
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>What it means:</strong> When women unconsciously believe and perpetuate sexist ideas 
                about women—including themselves.
              </p>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>Examples:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>"I'm not like other girls" (implying other women are inferior)</li>
                <li>Judging women for their clothing, makeup, or sexual choices</li>
                <li>Believing you're "too emotional" or "too sensitive"</li>
                <li>Competing with other women instead of supporting them</li>
                <li>Thinking "I can't do math/drive/lead" because you're a woman</li>
                <li>Policing other women's femininity or feminism</li>
              </ul>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Why it happens:</strong> We've all been raised in a patriarchal society. We've absorbed 
                  these messages since childhood. Recognizing internalized misogyny is the first step to unlearning it.
                </p>
              </div>
            </div>

            {/* Intersectionality */}
            <div className="bg-teal-50 rounded-xl p-6 border-l-4 border-teal-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                <i className="ri-links-line text-teal-600 mr-2"></i>
                Intersectionality
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>What it means:</strong> The interconnected nature of social categorizations such as race, 
                class, gender, sexuality, disability, etc., creating overlapping systems of discrimination or disadvantage.
              </p>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>Coined by:</strong> Kimberlé Crenshaw in 1989
              </p>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>Why it matters:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>A Black woman faces both racism AND sexism</li>
                <li>A disabled woman faces both ableism AND sexism</li>
                <li>A poor woman faces both classism AND sexism</li>
                <li>A trans woman faces both transphobia AND sexism</li>
                <li>These oppressions don't just add up—they multiply and interact</li>
              </ul>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Bottom line:</strong> Feminism that doesn't consider race, class, sexuality, disability, 
                  and other identities isn't real feminism. We must fight for ALL women.
                </p>
              </div>
            </div>

            {/* Glass Ceiling */}
            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                <i className="ri-building-line text-blue-600 mr-2"></i>
                Glass Ceiling
              </h3>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>What it means:</strong> An invisible barrier that prevents women and minorities from 
                advancing to top positions in their careers, regardless of their qualifications or achievements.
              </p>
              <p className="text-gray-700 mb-3 leading-relaxed">
                <strong>The reality:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Women hold only 10% of Fortune 500 CEO positions</li>
                <li>Women earn 82 cents for every dollar men earn</li>
                <li>Women are promoted less often than men with the same qualifications</li>
                <li>Mothers face the "motherhood penalty" (seen as less committed)</li>
                <li>Women are judged more harshly for the same behaviors as men</li>
              </ul>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Related terms:</strong> Glass cliff (women promoted to leadership during crises), 
                  sticky floor (women trapped in low-wage jobs), broken rung (women not promoted to first 
                  management level).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Types of Feminism */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-team-line text-3xl text-purple-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Types of Feminism</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-purple-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Liberal Feminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Focuses on achieving equality through legal and political reform. Believes women can achieve 
                equality within existing systems.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Radical Feminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Believes patriarchy is the root of all oppression and that fundamental societal change is needed. 
                Focuses on male violence and reproductive rights.
              </p>
            </div>

            <div className="bg-teal-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intersectional Feminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Centers the experiences of women who face multiple forms of oppression (race, class, sexuality, 
                disability). The most inclusive form of feminism.
              </p>
            </div>

            <div className="bg-pink-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Black Feminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Centers the unique experiences of Black women, who face both racism and sexism. Coined the term 
                "intersectionality."
              </p>
            </div>

            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Socialist Feminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Believes capitalism and patriarchy are interconnected. Focuses on economic inequality and 
                women's unpaid labor.
              </p>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Transfeminism</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Centers the experiences and rights of transgender women. Fights against transphobia within 
                and outside feminist movements.
              </p>
            </div>
          </div>
        </section>

        {/* Workplace & Economic Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-briefcase-line text-3xl text-blue-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Workplace & Economic Terms</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Gender Pay Gap</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The difference in earnings between women and men. Women earn approximately 82 cents for every 
                dollar men earn. For women of color, it's even worse.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Second Shift</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The unpaid domestic labor (cooking, cleaning, childcare) that women do after their paid work. 
                Women do 2-3 more hours of housework per day than men.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Emotional Labor</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The invisible work of managing emotions, relationships, and household logistics. Women are 
                expected to remember birthdays, plan events, manage schedules, and keep everyone happy.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pink Collar Jobs</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Jobs traditionally associated with women (nursing, teaching, childcare, administrative work). 
                These jobs are often undervalued and underpaid.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Motherhood Penalty</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Mothers earn less, are promoted less, and are seen as less competent than childless women. 
                Meanwhile, fathers get a "fatherhood bonus" and are seen as more responsible.
              </p>
            </div>
          </div>
        </section>

        {/* Violence & Safety Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-shield-cross-line text-3xl text-red-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Violence & Safety Terms</h2>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6 mb-6">
            <p className="text-sm text-red-800 font-semibold mb-2">
              ⚠️ Content Warning: This section discusses sexual violence and abuse.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Rape Culture</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                A society that normalizes, trivializes, and excuses sexual violence. Blames victims and protects perpetrators.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong>Examples:</strong> "What was she wearing?" "She was drunk." "Boys will be boys." 
                "She didn't fight back." Jokes about rape. Victim-blaming.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Victim Blaming</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Holding victims responsible for the violence committed against them instead of holding perpetrators 
                accountable. "Why didn't you leave?" "You should have known better."
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Gaslighting</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Psychological manipulation that makes you question your own reality, memory, or perceptions. 
                "You're too sensitive." "That never happened." "You're crazy."
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Coercive Control</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                A pattern of controlling behaviors that isolate, intimidate, and control a partner. Includes 
                monitoring, isolation, financial control, threats, and degradation.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Femicide</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The killing of women because they are women. Often committed by intimate partners or family members. 
                A woman is killed by a man every 11 minutes worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Everyday Sexism Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-chat-3-line text-3xl text-orange-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Everyday Sexism Terms</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mansplaining</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                When a man explains something to a woman in a condescending way, assuming she doesn't understand—
                even when she's an expert on the topic.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Manterrupting</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                When a man interrupts a woman while she's speaking. Studies show men interrupt women 33% more 
                than they interrupt other men.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Bropropriating</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                When a man takes credit for a woman's idea. She suggests something, it's ignored. He repeats 
                it 5 minutes later, everyone loves it.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Tone Policing</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Dismissing a woman's argument by criticizing how she said it instead of what she said. "You're 
                being too emotional." "Calm down." "You're being aggressive."
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Microaggressions</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Small, everyday slights and insults that communicate hostile or derogatory messages. "You're 
                pretty for a [race]." "You don't look like an engineer." "Are you sure you can handle this?"
              </p>
            </div>
          </div>
        </section>

        {/* Body & Reproductive Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-heart-pulse-line text-3xl text-pink-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Body & Reproductive Terms</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Bodily Autonomy</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The right to make decisions about your own body without coercion or interference. This includes 
                reproductive choices, medical decisions, and what you do with your body.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Reproductive Justice</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The right to have children, not have children, and parent children in safe and sustainable 
                communities. Goes beyond "pro-choice" to include economic and social justice.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Slut Shaming</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Criticizing or mocking women for their sexual behavior, clothing, or perceived promiscuity. 
                Used to control women's sexuality and bodies.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Body Policing</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Criticizing, controlling, or commenting on women's bodies, appearance, weight, or clothing. 
                "You'd be prettier if..." "Have you gained weight?" "That's too revealing."
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Period Poverty</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Lack of access to menstrual products, education, hygiene facilities, and waste management. 
                Affects millions of women and girls worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Signs to Watch For */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-eye-line text-3xl text-red-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Warning Signs of Sexism & Misogyny</h2>
          </div>

          <div className="bg-red-50 rounded-xl p-8">
            <p className="text-gray-700 mb-6 leading-relaxed">
              <strong>Learn to recognize these red flags in people, workplaces, relationships, and institutions:</strong>
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Dismissing women's expertise:</strong> "Are you sure?" "Let me explain..." 
                  "Actually..." when a woman is clearly knowledgeable
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Tone policing:</strong> Criticizing how a woman expresses herself instead of 
                  addressing her point
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Double standards:</strong> Behavior praised in men but criticized in women 
                  (assertive vs. bossy, confident vs. arrogant)
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Jokes at women's expense:</strong> "It's just a joke!" when called out for 
                  sexist humor
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Controlling behavior:</strong> Monitoring, isolating, financial control, 
                  constant criticism
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Victim blaming:</strong> "What was she wearing?" "Why didn't she leave?" 
                  "She should have known better."
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Gatekeeping feminism:</strong> "You're not a real feminist if..." Policing 
                  how women should be feminists
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Tokenism:</strong> Having one woman in leadership and claiming "diversity" 
                  while ignoring systemic issues
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Expecting emotional labor:</strong> Women expected to plan, organize, remember, 
                  and manage everyone's feelings
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>"Not all men":</strong> Derailing conversations about women's experiences by 
                  insisting not all men are like that
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How to Use These Terms */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-lightbulb-line text-3xl text-yellow-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">How to Use These Terms</h2>
          </div>

          <div className="bg-yellow-50 rounded-xl p-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Name What You're Experiencing
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  When you can name it, you can address it. "That's mansplaining." "That's gaslighting." 
                  "That's the glass ceiling." Naming gives you power.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Educate Others
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Share these terms with friends, family, colleagues. Help others recognize sexism when 
                  they see it. Knowledge is power.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Call It Out
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  When you see sexism, name it. "That's tone policing." "That's a microaggression." 
                  "That's victim blaming." Make the invisible visible.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Recognize Your Own Biases
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We all have internalized misogyny. Use these terms to recognize when you're perpetuating 
                  sexism—and work to unlearn it.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Build Solidarity
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Use these terms to connect with other women. "I experienced that too." "That's 
                  internalized misogyny—we all have it." Build community through shared language.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final Message */}
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <i className="ri-book-open-line text-5xl"></i>
          </div>
          <h2 className="text-3xl font-bold mb-4">Knowledge is Power</h2>
          <p className="text-xl mb-6 leading-relaxed max-w-2xl mx-auto">
            Now you have the language. Now you can name what you're experiencing. Now you can recognize 
            sexism when you see it. Now you can fight back.
          </p>
          <p className="text-lg font-semibold">
            Learn these terms. Use them. Share them. Teach them. 💜✊
          </p>
          <p className="text-lg mt-4">
            Because once you can name it, you can change it.
          </p>
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
            Share This Glossary
          </h3>
          <div className="flex justify-center gap-4 flex-wrap">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-facebook-fill"></i>
              Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-twitter-x-fill"></i>
              X (Twitter)
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-fill"></i>
              LinkedIn
            </a>
            <a
              href={`https://wa.me/?text=${shareTitle}%20${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill"></i>
              WhatsApp
            </a>
          </div>
        </div>

      </article>

      <Footer />
    </div>
  );
}